import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { BarChart3, Download, Clock, Car, Euro, FileSpreadsheet, FileText, Calculator } from 'lucide-react';
import { formatCurrency, formatNumber } from '@/lib/utils';
import { toast } from 'sonner';

export default function RapportenPage() {
  const { api, user } = useAuth();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [clients, setClients] = useState([]);
  const [period, setPeriod] = useState('month');
  const [clientId, setClientId] = useState('');
  const [exporting, setExporting] = useState(false);
  const [showBtwDialog, setShowBtwDialog] = useState(false);
  const [btwQuarter, setBtwQuarter] = useState('1');
  const [btwYear, setBtwYear] = useState(new Date().getFullYear().toString());

  useEffect(function() {
    async function load() {
      try {
        const res = await api.get('/clients');
        setClients(res.data);
      } catch (err) {
        console.error(err);
      }
    }
    load();
  }, []);

  useEffect(function() {
    async function load() {
      setLoading(true);
      try {
        let url = '/reports/summary?period=' + period;
        if (clientId) url = url + '&client_id=' + clientId;
        const res = await api.get(url);
        setReport(res.data);
      } catch (err) {
        toast.error(t('errors.somethingWentWrong'));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [period, clientId]);

  const canExport = user?.subscription_plan !== 'free';

  const handleExport = async (format) => {
    if (!canExport) {
      toast.error(t('errors.upgradeRequired'));
      return;
    }
    
    setExporting(true);
    try {
      let url = `/reports/export/${format}?period=${period}`;
      if (clientId) url += `&client_id=${clientId}`;
      
      const response = await api.get(url, { responseType: 'blob' });
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = `Urenbuddy_Export.${format === 'excel' ? 'xlsx' : 'csv'}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      const url_obj = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url_obj;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url_obj);
      
      toast.success(`${format === 'excel' ? 'Excel' : 'CSV'} ${i18n.language === 'en' ? 'downloaded!' : 'gedownload!'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.somethingWentWrong'));
    } finally {
      setExporting(false);
    }
  };

  const handleBtwExport = async () => {
    if (!canExport) {
      toast.error(t('errors.upgradeRequired'));
      return;
    }
    
    setExporting(true);
    try {
      const response = await api.get(`/reports/btw-export?quarter=${btwQuarter}&year=${btwYear}`, {
        responseType: 'blob'
      });
      
      const filename = `${i18n.language === 'en' ? 'VAT_Return' : 'BTW_Aangifte'}_Q${btwQuarter}_${btwYear}.xlsx`;
      const url_obj = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url_obj;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url_obj);
      
      toast.success(i18n.language === 'en' ? 'VAT return downloaded!' : 'BTW aangifte gedownload!');
      setShowBtwDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.somethingWentWrong'));
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="rapporten-page">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="h-32 bg-muted rounded-xl animate-pulse" />
          <div className="h-32 bg-muted rounded-xl animate-pulse" />
          <div className="h-32 bg-muted rounded-xl animate-pulse" />
        </div>
      </div>
    );
  }

  const clientOptions = clients.length > 0 ? clients : [];
  const breakdown = report?.client_breakdown || [];

  return (
    <div className="space-y-6" data-testid="rapporten-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-heading text-foreground">{t('reports.title')}</h1>
          <p className="text-muted-foreground">{t('reports.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            disabled={!canExport || exporting} 
            onClick={() => handleExport('csv')}
            data-testid="export-csv-btn"
          >
            <FileText className="w-4 h-4 mr-2" />{t('reports.exportCsv')}
          </Button>
          <Button 
            variant="outline" 
            disabled={!canExport || exporting} 
            onClick={() => handleExport('excel')}
            data-testid="export-excel-btn"
            className={canExport ? "bg-green-50 border-green-200 text-green-700 hover:bg-green-100 dark:bg-green-950 dark:border-green-800 dark:text-green-400" : ""}
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />{t('reports.exportExcel')}
          </Button>
          <Button 
            variant="outline" 
            disabled={!canExport || exporting} 
            onClick={() => setShowBtwDialog(true)}
            data-testid="export-btw-btn"
            className={canExport ? "bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100 dark:bg-purple-950 dark:border-purple-800 dark:text-purple-400" : ""}
          >
            <Calculator className="w-4 h-4 mr-2" />{t('reports.exportVat')}
          </Button>
        </div>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="space-y-2 flex-1">
              <Label>{t('reports.period')}</Label>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger data-testid="period-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="week">{t('dashboard.week')}</SelectItem>
                  <SelectItem value="month">{t('dashboard.month')}</SelectItem>
                  <SelectItem value="quarter">{t('dashboard.quarter')}</SelectItem>
                  <SelectItem value="year">{t('dashboard.year')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 flex-1">
              <Label>{t('common.client')} ({i18n.language === 'en' ? 'optional' : 'optioneel'})</Label>
              <Select value={clientId || "all"} onValueChange={(val) => setClientId(val === "all" ? "" : val)}>
                <SelectTrigger data-testid="client-filter-select">
                  <SelectValue placeholder={t('common.all') + ' ' + t('clients.title').toLowerCase()} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')} {t('clients.title').toLowerCase()}</SelectItem>
                  {clientOptions.map(function(c, idx) {
                    return <SelectItem key={String(idx)} value={c.client_id}>{c.naam}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-0 shadow-sm card-hover" data-testid="report-hours-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('reports.totalHours')}</p>
                <p className="text-3xl font-bold font-heading mt-2">{formatNumber(report?.total_hours || 0, 1)}</p>
                <p className="text-sm text-muted-foreground mt-1">{report?.time_entries_count || 0} {i18n.language === 'en' ? 'entries' : 'registraties'}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm card-hover" data-testid="report-km-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('reports.totalKilometers')}</p>
                <p className="text-3xl font-bold font-heading mt-2">{formatNumber(report?.total_kilometers || 0, 0)}</p>
                <p className="text-sm text-muted-foreground mt-1">{report?.mileage_entries_count || 0} {i18n.language === 'en' ? 'trips' : 'ritten'}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-xl flex items-center justify-center">
                <Car className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm card-hover" data-testid="report-revenue-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('reports.totalRevenue')}</p>
                <p className="text-3xl font-bold font-heading mt-2">{formatCurrency(report?.total_revenue || 0)}</p>
                <p className="text-sm text-muted-foreground mt-1">{i18n.language === 'en' ? 'excl. VAT' : 'excl. BTW'}</p>
              </div>
              <div className="w-12 h-12 bg-cyan-100 dark:bg-cyan-900 rounded-xl flex items-center justify-center">
                <Euro className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-heading">{t('reports.byClient')}</CardTitle>
        </CardHeader>
        <CardContent>
          {breakdown.length === 0 ? (
            <div className="text-center py-8">
              <BarChart3 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">{t('common.noData')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {breakdown.map(function(client, idx) {
                return (
                  <div key={String(idx)} className="flex items-center justify-between py-3 border-b last:border-0">
                    <div>
                      <p className="font-medium">{client.naam}</p>
                      <p className="text-sm text-muted-foreground">
                        {formatNumber(client.uren, 1)} {t('common.hours')} - {formatNumber(client.km, 0)} km
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-lg">{formatCurrency(client.omzet)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="text-center text-sm text-muted-foreground">
        {t('reports.period')}: {report?.start_date || '-'} {i18n.language === 'en' ? 'to' : 'tot'} {report?.end_date || '-'}
      </div>

      {/* BTW Export Dialog */}
      <Dialog open={showBtwDialog} onOpenChange={setShowBtwDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Calculator className="w-5 h-5 text-purple-600" />
              {t('reports.exportVat')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              {i18n.language === 'en' 
                ? 'Export a VAT overview for your quarterly tax return.' 
                : 'Exporteer een overzicht van je BTW voor de kwartaalaangifte bij de Belastingdienst.'}
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('reports.quarter')}</Label>
                <Select value={btwQuarter} onValueChange={setBtwQuarter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Q1 ({i18n.language === 'en' ? 'Jan-Mar' : 'jan-mrt'})</SelectItem>
                    <SelectItem value="2">Q2 ({i18n.language === 'en' ? 'Apr-Jun' : 'apr-jun'})</SelectItem>
                    <SelectItem value="3">Q3 ({i18n.language === 'en' ? 'Jul-Sep' : 'jul-sep'})</SelectItem>
                    <SelectItem value="4">Q4 ({i18n.language === 'en' ? 'Oct-Dec' : 'okt-dec'})</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('reports.year')}</Label>
                <Select value={btwYear} onValueChange={setBtwYear}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2024">2024</SelectItem>
                    <SelectItem value="2025">2025</SelectItem>
                    <SelectItem value="2026">2026</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="p-4 bg-purple-50 dark:bg-purple-950 rounded-lg border border-purple-200 dark:border-purple-800">
              <p className="text-sm text-purple-700 dark:text-purple-300">
                <strong>{i18n.language === 'en' ? 'Note:' : 'Let op:'}</strong> {i18n.language === 'en' 
                  ? 'This overview is based on your invoices in Urenbuddy. Always verify all data is correct before filing.'
                  : 'Dit overzicht is gebaseerd op je facturen in Urenbuddy. Controleer altijd of alle gegevens correct zijn voor je aangifte.'}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBtwDialog(false)}>{t('common.cancel')}</Button>
            <Button 
              className="bg-purple-600 hover:bg-purple-700" 
              onClick={handleBtwExport}
              disabled={exporting}
            >
              <Download className="w-4 h-4 mr-2" />
              {t('common.export')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
